// Profit per session (docs/limits-profit-feedback-receipts-acceptance-criteria.md P).
// Money arrives as strings.

export interface ProfitRow {
  key: string;
  label: string;
  revenue: string;
  llm_cost: string;
  whatsapp_cost: string;
  paystack_fees: string;
  purchase_margin: string;
  service_fees: string;
  gift_fees: string;
  profit: string;
  loss?: boolean;
}

export interface ProfitSummary {
  group: 'day' | 'user' | 'bill_type';
  from: string;
  to: string;
  rows: ProfitRow[];
  total: ProfitRow;
  sessions: number;
  sessions_with_purchase: number;
  purchases: number;
}

export interface SessionTimelineEvent {
  at: string;
  kind: string;
  cost: string;
  revenue: string;
  transaction_id: string | null;
  details: Record<string, any>;
}

export interface SessionDetail {
  id: string;
  user_name: string | null;
  chat_id: string;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
  messages_in: number;
  messages_out: number;
  llm_calls: number;
  purchases: number;
  llm_cost: string;
  whatsapp_cost: string;
  paystack_fees: string;
  cost: string;
  revenue: string;
  profit: string;
  langsmith_url: string | null;
  timeline: SessionTimelineEvent[];
  purchase_list: SessionPurchase[];
}

export interface SessionPurchase {
  id: string;
  reference: string;
  type: string;
  service_id: string;
  status: string;
  amount: string;
  service_fee: string;
  profit_loss: string | null;
  created_at: string;
}

export type PricedBillType = 'AIRTIME' | 'MOBILE_DATA' | 'CABLE_TV' | 'ELECTRICITY';

export interface UnitEconomicsRow {
  service_id: string;
  meter_class: string;
  bill_type: PricedBillType;
  rate_pct: string;
  cap: string | null;
  // How this product is priced today, e.g. "₦110 service fee on top".
  pricing: string;
  service_fee: string;
  profit_at: Record<string, string | null>;
  // Amounts we don't sell: "cap" (airtime cap) or "guard" (data loss guard).
  not_sold_at: Record<string, 'cap' | 'guard'>;
  break_even_from: string | null;
  // [from, to] among the amounts we sell that lose money; null = none do.
  loss_range: [string, string] | null;
  not_sold_range: [string, string] | null;
  worst_profit: string | null;
  purchases: number;
  average_amount: string | null;
  loss_share_pct: number | null;
}

export interface UnitEconomics {
  from: string;
  to: string;
  amounts: string[];
  pricing: PricingValues;
  rows: UnitEconomicsRow[];
}

// docs/pricing-acceptance-criteria.md P5. Money and percentages come back as
// strings, the loss guard as a boolean.
export interface PricingValues {
  AIRTIME_MAX_PER_PURCHASE: string;
  SERVICE_FEE_CABLE: string;
  ELECTRICITY_EXTRA_FEE: string;
  DATA_LOSS_GUARD_ENABLED: boolean;
  DATA_MIN_PROFIT: string;
  PAYSTACK_FEE_PCT: string;
  PAYSTACK_FEE_FLAT: string;
  PAYSTACK_FEE_FLAT_FROM: string;
  PAYSTACK_FEE_CAP: string;
  PAYSTACK_TRANSFER_FEE_PCT: string;
  PAYSTACK_TRANSFER_FEE_CAP: string;
}
export type PricingKey = keyof PricingValues;

export interface PricingSettings {
  values: PricingValues;
  file_defaults: PricingValues;
  updated_at: string | null;
  updated_by: string | null;
  // Off: customers pay by card only (backend TRANSFER_PAYMENTS_ENABLED).
  transfer_payments_enabled: boolean;
  history: { key: PricingKey; value: string; previous: string | null; reason: string | null; changed_by: string; changed_at: string }[];
}

export interface CostSettings {
  values: {
    COST_USD_NGN_RATE: number;
    LLM_PRICES_USD_PER_MTOK: Record<string, number[]>;
    WHATSAPP_RATES: Record<string, number>;
    WHATSAPP_RATE_CURRENCY: string;
    // The Claude model the chat agent uses; must have a row in LLM_PRICES_USD_PER_MTOK.
    AGENT_MODEL: string;
  };
  file_defaults: CostSettings['values'];
  updated_at: string | null;
  updated_by: string | null;
  history: { key: string; value: any; changed_by: string; changed_at: string }[];
}

export interface ProviderCheckCategory {
  category: string;
  reported_cost_ngn: string;
  our_cost_ngn: string;
  difference_pct: string | null;
  billed_messages: number;
  free_messages: number;
  our_messages: number;
}

export interface ProviderCheck {
  provider: string;
  day: string;
  reported_cost: string;
  currency: string;
  reported_cost_ngn: string | null;
  our_cost_ngn: string | null;
  difference_pct: string | null;
  flagged: boolean;
  categories: ProviderCheckCategory[];
  our_calls: number | null;
  checked_at: string | null;
}

// POST /admin/profit/provider-checks/run: what happened per provider.
export interface ProviderCheckRun {
  day: string;
  meta: { status: 'checked' | 'skipped' | 'error'; reason?: string; difference_pct?: string | null };
  anthropic: { status: 'checked' | 'skipped' | 'error'; reason?: string; difference_pct?: string | null };
}

export const KIND_LABELS: Record<string, string> = {
  LLM_CALL: 'Claude call',
  WHATSAPP_MESSAGE: 'WhatsApp message',
  PAYSTACK_FEE: 'Paystack fee',
  PURCHASE_MARGIN: 'Purchase margin',
  SERVICE_FEE: 'Service fee',
  GIFT_FEE: 'Dash fee',
  REFUND: 'Refund',
};

// GET /admin/transfers (pricing spec T5): money received by bank transfer.
export interface IncomingTransferRow {
  id: string;
  paystack_reference: string;
  amount: string;
  paystack_fee: string | null;
  status: 'MATCHED' | 'REFUNDING' | 'REFUNDED' | 'NEEDS_ACCOUNT' | 'REFUND_FAILED';
  note: string | null;
  sender_name: string | null;
  sender_bank: string | null;
  chat_id: string | null;
  order_code: string | null;
  order_amount: string | null;
  transaction_id: string | null;
  created_at: string;
}
